"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createFileUpload } from "@/services/file-upload.service";

/**
 * `processing` spans the server's own work — deriving the file attributes, and
 * whatever read-back the caller does in `onCompleted` — so a tile stays busy
 * until the stored file is actually usable, not just until the bytes are sent.
 */
export type MediaUploadStatus = "creating" | "uploading" | "processing" | "done" | "error";

export interface MediaUpload {
  key: string;
  file: File;
  /** Object URL of the local file — shown until the stored file is readable. */
  preview: string;
  mediaId?: string;
  status: MediaUploadStatus;
  progress: number;
  error?: string | null;
  uploadedBytes?: number;
  uploadSpeed?: number;
  eta?: number;
}

interface UseMediaUploadOptions {
  /**
   * Fires once the PyloMedia row exists but before its bytes land. The row is
   * already connectable at that point, which is what lets a form be saved while
   * an upload is still running.
   */
  onCreated?: (mediaId: string, file: File) => void;
  /** Awaited — the upload stays `processing` until it resolves. */
  onCompleted?: (mediaId: string, file: File) => void | Promise<void>;
}

/**
 * Uploading is the same gesture as picking: `createUpload` creates the library
 * row up front, so every uploaded file is a `PyloMedia` that a relation connects
 * to exactly like an existing one.
 */
export const useMediaUpload = ({ onCreated, onCompleted }: UseMediaUploadOptions = {}) => {
  const [uploads, setUploads] = useState<Array<MediaUpload>>([]);
  const nextKey = useRef(0);
  const previews = useRef<Set<string>>(new Set());

  const callbacks = useRef({ onCreated, onCompleted });
  useEffect(() => {
    callbacks.current = { onCreated, onCompleted };
  });

  useEffect(
    () => () => {
      previews.current.forEach((url) => URL.revokeObjectURL(url));
      previews.current.clear();
    },
    [],
  );

  const patch = useCallback((key: string, changes: Partial<MediaUpload>) => {
    setUploads((previous) =>
      previous.map((upload) => (upload.key === key ? { ...upload, ...changes } : upload)),
    );
  }, []);

  const send = useCallback(
    (upload: MediaUpload, mediaId: string, url: string) => {
      const startedAt = Date.now();
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        const elapsed = (Date.now() - startedAt) / 1000;
        const speed = elapsed > 0 ? event.loaded / elapsed : 0;
        const sent = event.loaded === event.total;
        patch(upload.key, {
          status: sent ? "processing" : "uploading",
          progress: Math.round((event.loaded / event.total) * 100),
          uploadedBytes: event.loaded,
          uploadSpeed: speed,
          eta: speed > 0 ? (event.total - event.loaded) / speed : 0,
        });
      };

      xhr.upload.onerror = () =>
        patch(upload.key, { status: "error", progress: 0, error: "Network error" });

      xhr.onreadystatechange = async () => {
        if (xhr.readyState !== 4) return;
        if (xhr.status < 200 || xhr.status >= 300) {
          patch(upload.key, {
            status: "error",
            progress: 0,
            error: `Upload failed: ${xhr.status} ${xhr.statusText}`,
          });
          return;
        }

        patch(upload.key, { status: "processing", progress: 100 });
        try {
          await callbacks.current.onCompleted?.(mediaId, upload.file);
          patch(upload.key, { status: "done" });
        } catch {
          patch(upload.key, { status: "error", error: "Uploaded, but could not be read back" });
        }
      };

      const body = new FormData();
      body.append("file", upload.file);
      xhr.open("POST", url);
      xhr.send(body);
    },
    [patch],
  );

  const start = useCallback(
    async (files: Array<File>, { replace = false }: { replace?: boolean } = {}) => {
      if (files.length === 0) return;

      const started = files.map<MediaUpload>((file) => {
        const preview = URL.createObjectURL(file);
        previews.current.add(preview);
        return {
          key: `upload-${nextKey.current++}`,
          file,
          preview,
          status: "creating",
          progress: 0,
        };
      });

      setUploads((previous) => (replace ? started : [...previous, ...started]));

      for (const upload of started) {
        let created: { id: string; url: string } | undefined;
        try {
          created = await createFileUpload();
        } catch {
          created = undefined;
        }

        if (!created?.url) {
          patch(upload.key, { status: "error", error: "Could not start the upload" });
          continue;
        }

        patch(upload.key, { mediaId: created.id, status: "uploading" });
        callbacks.current.onCreated?.(created.id, upload.file);
        send(upload, created.id, created.url);
      }
    },
    [patch, send],
  );

  const remove = useCallback((key: string) => {
    setUploads((previous) => {
      const target = previous.find((upload) => upload.key === key);
      if (target) {
        URL.revokeObjectURL(target.preview);
        previews.current.delete(target.preview);
      }
      return previous.filter((upload) => upload.key !== key);
    });
  }, []);

  const clear = useCallback(() => {
    setUploads((previous) => {
      previous.forEach((upload) => {
        URL.revokeObjectURL(upload.preview);
        previews.current.delete(upload.preview);
      });
      return [];
    });
  }, []);

  return { uploads, start, remove, clear };
};
